import { memo, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
} from 'recharts';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, groupByCategory, accentMap, currencyFmt, compactFmt } from './shared';
import type { DashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: Extract<DashboardWidget, { type: 'chart-bar' }>;
  sourceId: number | null | undefined;
}

function BarChartWidgetImpl({ widget, sourceId }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `chart-bar-${widget.reportCode}`);
  const { categoryField, valueField, aggregation = 'sum', title, subtitle, color, topN } = widget.config;

  const fill = typeof color === 'string' ? accentMap[color] ?? accentMap.blue : accentMap.blue;

  const series = useMemo(() => {
    const rows = data ?? [];
    const grouped = groupByCategory(rows, categoryField, valueField, aggregation);
    const sorted = grouped.sort((a, b) => b.value - a.value);
    return topN ? sorted.slice(0, topN) : sorted;
  }, [data, categoryField, valueField, aggregation, topN]);

  return (
    <div data-testid="chart-bar-widget">
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 260 }} />
        ) : series.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => compactFmt.format(v as number)}
                width={48}
              />
              <ChartTooltip
                formatter={(v) => currencyFmt.format(Number(v))}
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                  padding: '6px 10px',
                  boxShadow: '0 4px 16px var(--shadow-color-strong)',
                }}
                labelStyle={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
              />
              <Bar dataKey="value" fill={fill} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  );
}

export const BarChartWidget = memo(BarChartWidgetImpl);
